import * as Types from '../gql/_types.js';

import { MessageModule } from "./init";
import { mapArrayToStateObject } from "src/utils/state";
import { Action, AsyncAction, GraphQLSubscriptionResultsPayload } from 'src/state/_types';
import { subscriptions } from 'src/state/gql/_operations';
import { handleGraphqQLErrorResponse, handleGraphqQLSubscriptionErrorResponse } from 'src/state/gql/utils';
import { SetMetaInput } from 'src/state/Module';

const Module = MessageModule;

export const state = Module;

export const getItems: AsyncAction<Types.GetMessagesQueryVariables> = async ({state, effects}, payload) => {
    let finalPayload = Module.mergeQueryPayload(payload);
    state.messages.errors = [];
    state.messages.loading = true;
    try {
        let { limit, offset, orderBy } = state.messages.meta;
        let originalPayload = { limit, offset, order_by: orderBy };
        let { meta, items } = await effects.gql.queries.GetMessages({...originalPayload, ...finalPayload})
        state.messages.meta.total = meta.aggregate?.count || 0;
        state.messages.items = mapArrayToStateObject(items, 'id');
    } catch (e: any) {
        state.messages.errors = handleGraphqQLErrorResponse(e);
    }
    state.messages.loading = false;
}

export const resetState: Action = ({state}) => {
    state.messages.errors = []
    state.messages.loading = false
    state.messages.items = {}
    state.messages.currentItemId = null
}

export const resetMeta: Action = ({state}) => {
    state.messages.meta = {
        limit: Module.defaultLimit,
        sortOrder: Module.defaultSortOrder,
        sortKey: Module.defaultSortKey,
        page: 1,
        total: 0,
        offset: 0,
        orderBy: {}
    }
}

export const resetModule: AsyncAction = async ({actions}) => {
    actions.messages.resetMeta()
    actions.messages.resetState()
}

export const getMoreItems: AsyncAction = async ({state, effects}) => {
    let finalPayload = Module.mergeQueryPayload({});
    state.messages.errors = [];
    state.messages.loadingMore = true;
    try {
        let { limit, orderBy, page } = state.messages.meta;
        let newPage = ++page;
        let newOffset = ( limit * newPage ) - limit
        let originalPayload = { limit, offset: newOffset, order_by: orderBy };
        let { meta, items } = await effects.gql.queries.GetMessages({...originalPayload, ...finalPayload})
        state.messages.meta.total = meta.aggregate?.count || 0;
        state.messages.meta.page = newPage;
        state.messages.meta.offset = newOffset;
        state.messages.items = { ...state.messages.items, ...mapArrayToStateObject(items, 'id') };
    } catch (e: any) {
        state.messages.errors = handleGraphqQLErrorResponse(e);
    }
    state.messages.loadingMore = false;
}

export const getAllItems: AsyncAction<Types.GetMessagesQueryVariables> = async ({state, effects}, payload) => {
    state.messages.errors = [];
    state.messages.loading = true;
    try {
        let { meta, items } = await effects.gql.queries.GetMessages(payload)
        state.messages.meta.total = meta.aggregate?.count || 0;
        state.messages.items = mapArrayToStateObject(items, 'id');
    } catch (e: any) {
        state.messages.errors = handleGraphqQLErrorResponse(e);
    }
    state.messages.loading = false;
}

export const getItem: AsyncAction<Types.GetMessageQueryVariables,Types.GetMessageQuery['item']> = async ({state, effects}, payload) => {
    try {
        let { item } = await effects.gql.queries.GetMessage(payload);
        return item;
    } catch (e: any) {
        state.messages.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const loadItem: AsyncAction<Types.GetMessageQueryVariables> = async ({state, actions}, payload) => {
    state.messages.loading = true;
    try {
        let item = await actions.messages.getItem(payload);
        if (item) {
            state.messages.currentItemId = item.id;
            state.messages.items[item.id] = item;
        }
    } catch (e: any) {
        state.messages.errors = handleGraphqQLErrorResponse(e);
    }
    state.messages.loading = false;
}

export const selectItem: Action<string> = async ({state}, payload) => {
    state.messages.currentItemId = payload
}

export const deselectItem: Action = async ({state}) => {
    state.messages.currentItemId = null
}

export const setMeta: Action<SetMetaInput<Types.MessageFragment>> = async ({state}, meta) => {
    state.messages.meta = {
        ...state.messages.meta,
        ...meta
    }
}


export const createItem: AsyncAction<Types.CreateMessageMutationVariables['object'],Types.CreateMessageMutation['item']> = async ({state, effects}, payload) => {
    let finalPayload = Module.mergeCreatePayload<Types.CreateMessageMutationVariables['object']>(payload);
    state.messages.errors = [];
    try {
        let { item } = await effects.gql.mutations.CreateMessage({object:finalPayload});
        return item;
    } catch (e: any) {
        state.messages.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const updateItem: AsyncAction<Types.UpdateMessageMutationVariables,Types.UpdateMessageMutation['item']> = async ({state, effects}, payload) => {
    state.messages.errors = [];
    try {
        let { item } = await effects.gql.mutations.UpdateMessage(payload);
        return item;
    } catch (e: any) {
        state.messages.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const deleteItem: AsyncAction<Types.DeleteMessageMutationVariables,Types.DeleteMessageMutation['item']> = async ({state, effects}, payload) => {
    state.messages.errors = [];
    try {
        let { item } = await effects.gql.mutations.DeleteMessage(payload);
        return item;
    } catch (e: any) {
        state.messages.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const syncGetItemsMeta: Action<Types.SyncGetMessagesMetaSubscriptionVariables> = async ({actions, effects}, {where}) => {
    effects.subscriptions.subscribe<Types.SyncGetMessagesMetaSubscription, Types.SyncGetMessagesMetaSubscriptionVariables>(
        'syncGetMessagesMeta', 
        subscriptions.SyncGetMessagesMeta, 
        { where }, 
        actions.messages.onItemsMeta
    );
}

export const unsubItemsMeta: Action = ({effects}) => {
    effects.subscriptions.unsubscribe('syncGetMessagesMeta');
}

export const onItemsMeta: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetMessagesMetaSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.messages.errors = [ ...state.messages.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data) {
        state.messages.meta.total = payload.data.meta.aggregate?.count || 0;
    }    
}

export const syncGetItems: Action<Types.SyncGetMessagesSubscriptionVariables> = async ({state, actions, effects}, payload) => {
    let { limit, offset, orderBy } = state.messages.meta;
    let originalPayload = { limit, offset, order_by: orderBy };
    state.messages.loading = true;
    effects.subscriptions.subscribe<Types.SyncGetMessagesSubscription, Types.SyncGetMessagesSubscriptionVariables>(
        'syncGetMessages', 
        subscriptions.SyncGetMessages, 
        {...originalPayload, ...payload}, 
        actions.messages.onItemsData,
        () => { state.messages.loading = false }
    );
}

export const unsubItemsData: Action = ({state,effects}) => {
    effects.subscriptions.unsubscribe('syncGetMessages');
    state.messages.items = {};
}

export const onItemsData: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetMessagesSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.messages.errors = [ ...state.messages.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data&&payload.data.items) {
        state.messages.items = mapArrayToStateObject(payload.data.items, 'id');
    }  
}

export const subscribeToItems: Action<Types.SyncGetMessagesSubscriptionVariables> = async ({actions}, payload) => {
    let finalPayload = Module.mergeQueryPayload(payload);
    actions.messages.syncGetItems(finalPayload);
    actions.messages.syncGetItemsMeta(finalPayload);
}

export const unsubscribeFromItems: Action = ({actions}) => {
    actions.messages.unsubItemsData();
    actions.messages.unsubItemsMeta();
}

export const syncGetItem: Action<Types.SyncGetMessageSubscriptionVariables> = async ({state, actions, effects}, payload) => {
    state.messages.loading = true;
    effects.subscriptions.subscribe<Types.SyncGetMessageSubscription, Types.SyncGetMessageSubscriptionVariables>(
        'syncGetMessage', 
        subscriptions.SyncGetMessage, 
        payload, 
        actions.messages.onItemData,
        () => { state.messages.loading = false }
    );
}

export const unsubItemData: Action = ({state,effects}) => {
    effects.subscriptions.unsubscribe('syncGetMessage');
    state.messages.currentItemId = null;
}

export const onItemData: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetMessageSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.messages.errors = [ ...state.messages.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data&&payload.data.item) {
        state.messages.currentItemId = payload.data.item.id;
        state.messages.items[payload.data.item.id] = payload.data.item;
    }
}

export const subscribeToItem: Action<Types.SyncGetMessageSubscriptionVariables> = async ({actions}, payload) => {
    actions.messages.syncGetItem(payload);
}

export const unsubscribeFromItem: Action = ({actions}) => {
    actions.messages.unsubItemData();
}

export const actions = {
    getItems,
    getAllItems,
    resetState,
    resetMeta,
    getMoreItems,
    resetModule,
    getItem,
    loadItem,
    selectItem,
    deselectItem,
    setMeta,
    createItem,
    updateItem,
    deleteItem,
    syncGetItemsMeta,
    unsubItemsMeta,
    onItemsMeta,
    syncGetItems,
    unsubItemsData,
    onItemsData,
    subscribeToItems,
    unsubscribeFromItems,
    syncGetItem,
    unsubItemData,
    onItemData,
    subscribeToItem,
    unsubscribeFromItem
};